import { Image, Page, Text, View } from '@react-pdf/renderer'
import { PageFooter } from 'features/reporting/pdfGeneration/components/shared/PageFooter'
import { PageHeader } from 'features/reporting/pdfGeneration/components/shared/PageHeader'
import { PageSubheader } from 'features/reporting/pdfGeneration/components/shared/PageSubHeader'
import { PDFStyles } from 'features/reporting/pdfGeneration/styles/PDFStyles'
import { WeldIndication } from 'pages/reporting/reports/raut/hooks/useWeldIndications'
import { RautFormDataState } from 'pages/reporting/reports/raut/rautReportData'
import { getIndicationKey } from 'pages/reporting/reports/raut/utils/indicationUtils'
import React, { useMemo, useRef } from 'react'

interface WeldIndicationVisualizationsProps {
  weldIndicationsWithImages: WeldIndication[]
  renderedWeldImageUrls: Record<string, string>
  formData: RautFormDataState
  fileNamesWithoutIndications: string[]
}

/**
 * Renders saved weld indication visualizations in the PDF
 * Each indication gets its own page with the saved S-scan/C-scan/D-scan composite image
 */
export const WeldIndicationVisualizations: React.FC<
  WeldIndicationVisualizationsProps
> = ({
  weldIndicationsWithImages,
  renderedWeldImageUrls,
  formData,
  fileNamesWithoutIndications,
}) => {
  const hasRegisteredFirstPage = useRef(false)

  const fileNamesWithScans = useMemo(() => {
    return fileNamesWithoutIndications.filter((fileName) => {
      // Use sanitized key without .extension to check if scan is saved
      const key = getIndicationKey(fileName)
      return !!renderedWeldImageUrls[key]
    })
  }, [fileNamesWithoutIndications, renderedWeldImageUrls])

  if (
    weldIndicationsWithImages.length === 0 &&
    fileNamesWithScans.length === 0
  ) {
    return null
  }

  // Sort weld indications by indication number (extracted from title)
  const sortedWeldIndications = [...weldIndicationsWithImages].sort((a, b) => {
    // Extract indication number from title (e.g., "Indication 1" -> 1)
    const extractNumber = (title: string): number => {
      const match = title.match(/Indication\s+(\d+)/i)
      return match ? parseInt(match[1], 10) : 0
    }

    const aNum = extractNumber(a.title)
    const bNum = extractNumber(b.title)

    return aNum - bNum
  })

  return (
    <>
      {/* Render indication visualizations */}
      {sortedWeldIndications.map((indication, index) => {
        const imageUrl = renderedWeldImageUrls[indication.id]

        if (!imageUrl) return null

        // Find the scan info from weldScanData using the file_name
        const scanInfo = formData.weldScanData?.scans.find(
          (scan) => scan.fileName === indication?.customInfo?.file_name
        )
        const scanLocation = scanInfo?.scanLocation || ''
        const scanLabel =
          scanInfo?.scanLabel || indication.customInfo.file_name || ''
        const scanHeader = `Scan: ${scanLocation ? `${scanLocation} - ` : ''}${scanLabel}`

        return (
          <Page
            key={indication.id}
            size='A4'
            orientation='landscape'
            style={PDFStyles.page}
            id={
              index === 0
                ? 'weldIndicationVisualizations'
                : `weldIndicationVisualization${index + 1}`
            }
            wrap={false}>
            {/* Hidden text for page tracking - only register first page */}
            {index === 0 && !hasRegisteredFirstPage.current && (
              <Text
                render={() => {
                  hasRegisteredFirstPage.current = true
                  return ''
                }}
              />
            )}

            <PageHeader title='PAUT Weld Indication Visualizations' />

            <View
              style={{
                margin: 0,
                padding: 0,
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
              }}>
              {/* Scan info header */}
              <View style={{ alignItems: 'flex-start', marginBottom: 4 }}>
                <Text style={{ fontSize: 11, fontWeight: 'bold' }}>
                  {scanHeader}
                </Text>
              </View>

              <View style={{ alignItems: 'flex-start' }}>
                <PageSubheader
                  title={indication.title}
                  align='left'
                  color='black'
                />
              </View>

              <View
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                }}>
                <Image
                  src={imageUrl}
                  style={{
                    width: 1500,
                    height: 500,
                    objectFit: 'contain',
                  }}
                  cache={false}
                />
              </View>
            </View>

            <PageFooter
              jobNumber={formData.coverPageData.geckoJobId}
              revisionNumber={formData.revisionMetadata.revisionNumber}
              prelim={
                !(
                  formData.signoff?.reviewerSignoff &&
                  formData.signoff.supervisorSignoff
                )
              }
            />
          </Page>
        )
      })}

      {/* Render file scan visualizations (files without indications) */}
      {fileNamesWithScans.map((fileName, index) => {
        // Use sanitized key (without extension) to look up the image URL
        const key = getIndicationKey(fileName)
        const imageUrl = renderedWeldImageUrls[key]

        if (!imageUrl) return null

        const scanInfo = formData.weldScanData?.scans.find(
          (scan) => scan.fileName === fileName
        )
        const scanLocation = scanInfo?.scanLocation || ''
        const scanLabel = scanInfo?.scanLabel || fileName
        const scanHeader = `Scan: ${scanLocation ? `${scanLocation} - ` : ''}${scanLabel}`

        const pageIndex = sortedWeldIndications.length + index

        return (
          <Page
            key={`file-${fileName}`}
            size='A4'
            orientation='landscape'
            style={PDFStyles.page}
            id={
              pageIndex === 0
                ? 'weldIndicationVisualizations'
                : `weldIndicationVisualization${pageIndex + 1}`
            }
            wrap={false}>
            {/* Hidden text for page tracking - only register first page */}
            {pageIndex === 0 && !hasRegisteredFirstPage.current && (
              <Text
                render={() => {
                  hasRegisteredFirstPage.current = true
                  return ''
                }}
              />
            )}

            <PageHeader title='PAUT Weld Indication Visualizations' />

            <View
              style={{
                margin: 0,
                padding: 0,
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
              }}>
              {/* Scan info header */}
              <View style={{ alignItems: 'flex-start', marginBottom: 4 }}>
                <Text style={{ fontSize: 11, fontWeight: 'bold' }}>
                  {scanHeader}
                </Text>
              </View>

              <View
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                }}>
                <Image
                  src={imageUrl}
                  style={{
                    width: 1500,
                    height: 500,
                    objectFit: 'contain',
                  }}
                  cache={false}
                />
              </View>
            </View>

            <PageFooter
              jobNumber={formData.coverPageData.geckoJobId}
              revisionNumber={formData.revisionMetadata.revisionNumber}
              prelim={
                !(
                  formData.signoff?.reviewerSignoff &&
                  formData.signoff.supervisorSignoff
                )
              }
            />
          </Page>
        )
      })}
    </>
  )
}
